import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { NameListManager } from "@/inventory/components/name-list-manager"
import {
  createCarrierFn,
  createVendorFn,
  listCarriersFn,
  listVendorsFn,
  updateCarrierFn,
  updateVendorFn,
} from "@/inventory/rpc"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"

export const Route = createFileRoute("/_inventory-layout/inventory/settings")({
  component: SettingsPage,
})

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function SettingsPage() {
  const queryClient = useQueryClient()
  const [carrierError, setCarrierError] = useState<string | null>(null)
  const [vendorError, setVendorError] = useState<string | null>(null)

  const carriersQuery = useQuery({
    queryKey: ["carriers", { includeInactive: true }],
    queryFn: () => listCarriersFn({ data: { includeInactive: true } }),
  })

  const vendorsQuery = useQuery({
    queryKey: ["vendors", { includeInactive: true }],
    queryFn: () => listVendorsFn({ data: { includeInactive: true } }),
  })

  const createCarrier = useMutation({
    mutationFn: (name: string) => createCarrierFn({ data: { name } }),
    onSuccess: async () => {
      setCarrierError(null)
      await queryClient.invalidateQueries({ queryKey: ["carriers"] })
    },
    onError: (error) => setCarrierError(getErrorMessage(error)),
  })

  const updateCarrier = useMutation({
    mutationFn: (input: { id: number; isActive: boolean }) =>
      updateCarrierFn({ data: input }),
    onSuccess: async () => {
      setCarrierError(null)
      await queryClient.invalidateQueries({ queryKey: ["carriers"] })
    },
    onError: (error) => setCarrierError(getErrorMessage(error)),
  })

  const createVendor = useMutation({
    mutationFn: (name: string) => createVendorFn({ data: { name } }),
    onSuccess: async () => {
      setVendorError(null)
      await queryClient.invalidateQueries({ queryKey: ["vendors"] })
    },
    onError: (error) => setVendorError(getErrorMessage(error)),
  })

  const updateVendor = useMutation({
    mutationFn: (input: { id: number; isActive: boolean }) =>
      updateVendorFn({ data: input }),
    onSuccess: async () => {
      setVendorError(null)
      await queryClient.invalidateQueries({ queryKey: ["vendors"] })
    },
    onError: (error) => setVendorError(getErrorMessage(error)),
  })

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Settings</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Manage carriers and vendors used across inventory.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Carriers</CardTitle>
            <CardDescription>
              Mobile carriers associated with SIMs and devices.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NameListManager
              title="Carrier name"
              placeholder="e.g. Verizon"
              emptyMessage="No carriers yet."
              items={carriersQuery.data ?? []}
              isLoading={carriersQuery.isLoading}
              isAdding={createCarrier.isPending}
              isTogglingId={
                updateCarrier.isPending
                  ? (updateCarrier.variables?.id ?? null)
                  : null
              }
              error={
                carrierError ??
                (carriersQuery.isError
                  ? getErrorMessage(carriersQuery.error)
                  : null)
              }
              onAdd={(name) => createCarrier.mutateAsync(name)}
              onToggleActive={(id, isActive) =>
                updateCarrier.mutateAsync({ id, isActive })
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Vendors</CardTitle>
            <CardDescription>
              Suppliers used when receiving inventory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NameListManager
              title="Vendor name"
              placeholder="e.g. Acme Supply"
              emptyMessage="No vendors yet."
              items={vendorsQuery.data ?? []}
              isLoading={vendorsQuery.isLoading}
              isAdding={createVendor.isPending}
              isTogglingId={
                updateVendor.isPending
                  ? (updateVendor.variables?.id ?? null)
                  : null
              }
              error={
                vendorError ??
                (vendorsQuery.isError
                  ? getErrorMessage(vendorsQuery.error)
                  : null)
              }
              onAdd={(name) => createVendor.mutateAsync(name)}
              onToggleActive={(id, isActive) =>
                updateVendor.mutateAsync({ id, isActive })
              }
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
